import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { RichBlockType } from '@pure1/data';

/**
 * The loading placeholder for a block whose position and type are known but whose payload has not
 * arrived (FR-040). Typed rather than one generic bar so the prose does not jump when it lands.
 *
 * Deliberately no timer or timeout: pending versus orphaned is decided by stream end, never by
 * elapsed time (R3, FR-040), so the template that renders this stops rendering it. An
 * unrecognised type degrades to a generic shape rather than rendering nothing.
 */
@Component({
    selector: 'copilot-rich-block-skeleton',
    templateUrl: './rich-block-skeleton.component.html',
    styleUrl: './rich-block-skeleton.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    preserveWhitespaces: false,
    host: {
        // The stylesheet sizes each shape from this, and tests identify the pending type by it.
        '[attr.data-block-type]': 'type()',
    },
})
export class RichBlockSkeletonComponent {
    /** The type the anchor declared. Typed as {@link RichBlockType} but not trusted to be one. */
    readonly type = input.required<RichBlockType>();

    /** Screen-reader labels. A loading state that only exists visually is not a loading state. */
    private static readonly LABELS: Record<string, string> = {
        table: 'Loading a table block',
        chart: 'Loading a chart block',
        stat_list: 'Loading a statistics block',
        task_list: 'Loading a task list block',
        actions: 'Loading an actions block',
        callout: 'Loading a callout block',
        select: 'Loading a selection block',
    };

    protected readonly label = computed(() => RichBlockSkeletonComponent.LABELS[this.type()] ?? 'Loading a block');

    /** Row, tile, task and option counts are fixed: the payload's real length is not yet known. */
    protected readonly rows = [0, 1, 2, 3];
    protected readonly tiles = [0, 1, 2, 3];
    protected readonly tasks = [0, 1, 2];
    protected readonly options = [0, 1, 2];
    protected readonly buttons = [0, 1];
}
